import { axiosInstance } from "api/axios.instance"
import type {
	TCreateQuotationRequest,
	TGetComponentOptionsParams,
	TQuotationDetailsWordGenerationResponse,
	TUpdateQuotationConfigurationRequest,
	TQuotationResultPdfGenerationResponse,
} from "types/quotations.type"
import type {
	TConnectFurnitureRequest,
	TMoveFurnitureRequest,
	TMoveFurnitureResponse,
	TCreateErefRequest,
	TCreateFurnitureRequest,
	TCreateFurnitureResponse,
	TEref,
	TUpdateErefRequest,
	TUpdateErefResponse,
	TUpdateErefStatusRequest,
	TUpdateErefCommentRequest,
	TErefDeleteResponse,
	TUpdateEcallRequest,
	TCreateEcallRequest,
	TUpdateEcallResponse,
	TEcallDeleteResponse,
	TCreateEcallElevationRequest,
	TCreateEcallElevationResponse,
	TCreateEcallResponse,
	TDeleteEcallElevationResponse,
	TUpdateEcallElevationsRequest,
	TUpdateEcallElevationsResponse,
	TAutoConnectEcallElevationRequest,
	TAutoConnectEcallElevationResponse,
	TAutoConnectErefEcallsRequest,
	TAutoConnectErefEcallsResponse,
	TUpdateFurnitureCategoryRequest,
	TUpdateFurnitureFixedPriceRequest,
	TUpdateFurnitureCountertopSeriesRequest,
	TUpdateFurnitureCountertopSeriesResponse,
	TUpdateFurnitureCountertopAttachmentsRequest,
	TUpdateFurnitureCountertopAttachmentsResponse,
	TUpdateFurnitureRequest,
	TUpdateFurnitureResponse,
	TSelectArbitraryModelNumberRequest,
	TLinkFurnitureCustomModelNumberRequest,
	TUpdateFurnitureConfigRequest,
	TUpdateFurnitureCommentRequest,
	TUpdateFurnitureCustomizationRequest,
	TUpdateFurnitureCustomizationResponse,
	TDeleteFurnitureCustomizationResponse,
	TUpdateFurnitureStatusRequest,
	TUpdateErefConfigurationRequest,
	TDuplicateFurnitureRequest,
	TDuplicateFurnitureResponse,
	TBatchUpdateFurnitureStatusRequest,
	TBatchUpdateFurnitureStatusResponse,
	TBatchDeleteFurnitureResponse,
	TBatchUpdateFurnitureRequest,
	TBatchUpdateFurnitureResponse,
	TBatchUpdateFurnitureCategoryRequest,
	TBatchUpdateFurnitureCustomModelNumberRequest,
	TBatchUpdateFurnitureCustomModelNumberResponse,
	TBatchUpdateFurnitureCategoryResponse,
	TBatchSelectArbitraryModelNumberRequest,
	TBatchSelectArbitraryModelNumberResponse,
	TBatchUpdateFurnitureConfigRequest,
	TBatchUpdateFurnitureConfigResponse,
	TBatchUpdateFurnitureCountertopAttachmentsRequest,
	TBatchUpdateFurnitureCountertopAttachmentsResponse,
	TBatchUpdateFurnitureFixedPriceRequest,
	TBatchUpdateFurnitureFixedPriceResponse,
	TBatchUpdateFurnitureCommentRequest,
	TBatchUpdateFurnitureCommentResponse,
	TBatchDeleteFurnitureCommentResponse,
	TBatchUpdateFurnitureCustomizationRequest,
	TBatchUpdateFurnitureCustomizationResponse,
	TBatchDeleteFurnitureCustomizationResponse,
	TUpdatePageRequest,
	TUpdatePageResponse,
} from "types/elevationReferences.type"

// Generous enough for the rare slow generation (~20s worst case), but still finite so a genuinely
// stuck request resolves into the timeout message instead of spinning forever.
const RESULT_CSV_TIMEOUT = 60_000

// The on-demand result PDF's start/poll calls (#1401) are themselves fast — they return a small job-
// status body, not the file — so this only needs to cover an ordinary slow request, not the build
// itself (which runs in the background and is followed by polling, not by this timeout).
const RESULT_PDF_REQUEST_TIMEOUT = 30_000

// The on-demand Word file's start/poll calls (#1448) are themselves fast — they return a small job-
// status body, not the file — so this only needs to cover an ordinary slow request, not the build
// itself (which runs in the background and is followed by polling, not by this timeout).
const DETAILS_WORD_REQUEST_TIMEOUT = 30_000

class QuotationsService {
	async getQuotations(drawingId: number) {
		return axiosInstance.get(`drawings/${drawingId}/quotations/`)
	}

	async getQuotation(quotationId: number) {
		return axiosInstance.get(`quotations/${quotationId}/`)
	}

	async createQuotation(body: TCreateQuotationRequest) {
		return axiosInstance.post(`quotations/`, body)
	}

	async updateQuotationConfiguration(quotationId: number, body: TUpdateQuotationConfigurationRequest) {
		return axiosInstance.patch(`quotations/${quotationId}/`, body)
	}

	async getComponentOptions({ series_id, components, quotation_id }: TGetComponentOptionsParams) {
		return axiosInstance.get(`quotations/component-options/`, {
			params: {
				series_id,
				components: components.join(","),
				...(quotation_id ? { quotation_id } : {}),
			},
		})
	}

	async renameQuotation(quotationId: number, name: string) {
		return axiosInstance.patch(`quotations/${quotationId}/name/`, { name })
	}

	async connectFurnitureToEref(quotationId: number, erefId: number, body: TConnectFurnitureRequest) {
		return axiosInstance.post(`quotations/${quotationId}/furniture/eref/${erefId}/connect/`, body)
	}

	async disconnectFurnitureFromEref(quotationId: number, erefId: number, body: TConnectFurnitureRequest) {
		return axiosInstance.post(`quotations/${quotationId}/furniture/eref/${erefId}/disconnect/`, body)
	}

	async updatePage(quotationId: number, pageInfoId: number, body: TUpdatePageRequest) {
		return axiosInstance.patch<TUpdatePageResponse>(`quotations/${quotationId}/pages/${pageInfoId}/`, body)
	}

	// Transactional reassign of furniture to `erefId`, regardless of their current link. Replaces the
	// disconnect-then-connect pair for the "connect / move to elevation" flow (toolbar + context menu).
	async moveFurnitureToEref(quotationId: number, erefId: number, body: TMoveFurnitureRequest) {
		return axiosInstance.post<TMoveFurnitureResponse>(`quotations/${quotationId}/furniture/eref/${erefId}/move/`, body)
	}

	// Create an eref from an area drawn on the drawing (REQ-292). Responds 201 with the whole eref.
	async createEref(quotationId: number, body: TCreateErefRequest) {
		return axiosInstance.post<TEref>(`quotations/${quotationId}/eref/`, body)
	}

	async createFurniture(quotationId: number, body: TCreateFurnitureRequest) {
		return axiosInstance.post<TCreateFurnitureResponse>(`quotations/${quotationId}/furniture/`, body)
	}

	async updateEref(quotationId: number, erefId: number, body: TUpdateErefRequest) {
		return axiosInstance.patch<TUpdateErefResponse>(`quotations/${quotationId}/eref/${erefId}/`, body)
	}

	async updateErefStatus(quotationId: number, erefId: number, body: TUpdateErefStatusRequest) {
		return axiosInstance.patch(`quotations/${quotationId}/eref/${erefId}/status/`, body)
	}

	async updateErefComment(quotationId: number, erefId: number, body: TUpdateErefCommentRequest) {
		return axiosInstance.put(`quotations/${quotationId}/eref/${erefId}/comment/`, body)
	}

	async deleteEref(quotationId: number, erefId: number) {
		return axiosInstance.delete<TErefDeleteResponse>(`quotations/${quotationId}/eref/${erefId}/`)
	}

	async deleteErefComment(quotationId: number, erefId: number, version: number) {
		return axiosInstance.delete(`quotations/${quotationId}/eref/${erefId}/comment/`, {
			params: { version },
		})
	}

	async updateEcall(quotationId: number, ecallId: number, body: TUpdateEcallRequest) {
		return axiosInstance.patch<TUpdateEcallResponse>(`quotations/${quotationId}/ecall/${ecallId}/`, body)
	}

	async createEcall(quotationId: number, body: TCreateEcallRequest) {
		return axiosInstance.post<TCreateEcallResponse>(`quotations/${quotationId}/ecall/`, body)
	}

	async deleteEcall(quotationId: number, ecallId: number) {
		return axiosInstance.delete<TEcallDeleteResponse>(`quotations/${quotationId}/ecall/${ecallId}/`)
	}

	async createEcallElevation(quotationId: number, ecallId: number, body: TCreateEcallElevationRequest) {
		return axiosInstance.post<TCreateEcallElevationResponse>(`quotations/${quotationId}/ecall/${ecallId}/elevations/`, body)
	}

	async deleteEcallElevation(quotationId: number, ecallId: number, ecallElevationId: number) {
		return axiosInstance.delete<TDeleteEcallElevationResponse>(
			`quotations/${quotationId}/ecall/${ecallId}/elevations/${ecallElevationId}/`,
		)
	}

	async updateEcallElevations(quotationId: number, ecallId: number, body: TUpdateEcallElevationsRequest) {
		return axiosInstance.patch<TUpdateEcallElevationsResponse>(
			`quotations/${quotationId}/ecall/${ecallId}/elevations/`,
			body,
		)
	}

	async autoConnectEcallElevation(
		quotationId: number,
		ecallId: number,
		ecallElevationId: number,
		body: TAutoConnectEcallElevationRequest,
	) {
		return axiosInstance.post<TAutoConnectEcallElevationResponse>(
			`quotations/${quotationId}/ecall/${ecallId}/elevations/${ecallElevationId}/auto-connect/`,
			body,
		)
	}

	async updateErefConfiguration(quotationId: number, erefId: number, body: TUpdateErefConfigurationRequest) {
		return axiosInstance.patch(`quotations/${quotationId}/eref/${erefId}/configuration/`, body)
	}

	async autoConnectErefEcalls(quotationId: number, erefId: number, body: TAutoConnectErefEcallsRequest) {
		return axiosInstance.post<TAutoConnectErefEcallsResponse>(
			`quotations/${quotationId}/eref/${erefId}/auto-connect/`,
			body,
		)
	}

	async updateFurnitureCategory(quotationId: number, furnitureId: number, body: TUpdateFurnitureCategoryRequest) {
		return axiosInstance.patch(`quotations/${quotationId}/furniture/${furnitureId}/related-category/`, body)
	}

	async updateFurnitureFixedPrice(quotationId: number, furnitureId: number, body: TUpdateFurnitureFixedPriceRequest) {
		return axiosInstance.patch(`quotations/${quotationId}/furniture/${furnitureId}/fixed-price/`, body)
	}

	/** REQ-405. Dedicated endpoint — the general furniture PATCH does not carry this field. */
	async updateFurnitureCountertopSeries(
		quotationId: number,
		furnitureId: number,
		body: TUpdateFurnitureCountertopSeriesRequest,
	) {
		return axiosInstance.patch<TUpdateFurnitureCountertopSeriesResponse>(
			`quotations/${quotationId}/furniture/${furnitureId}/countertop-series/`,
			body,
		)
	}

	/**
	 * REQ-449. Sets the Grommets / Wall Cleats counts on a single countertop; each count is optional.
	 */
	async updateFurnitureCountertopAttachments(
		quotationId: number,
		furnitureId: number,
		body: TUpdateFurnitureCountertopAttachmentsRequest,
	) {
		return axiosInstance.patch<TUpdateFurnitureCountertopAttachmentsResponse>(
			`quotations/${quotationId}/furniture/${furnitureId}/countertop-attachments/`,
			body,
		)
	}

	async updateFurniture(quotationId: number, furnitureId: number, body: TUpdateFurnitureRequest) {
		return axiosInstance.patch(`quotations/${quotationId}/furniture/${furnitureId}/`, body)
	}

	/**
	 * Selects a model number from ANY category (REQ-398). `updateFurniture` rejects a
	 * `selected_model_number_id` whose category does not match the furniture's current
	 * `related_category`; this endpoint accepts it and moves the furniture's category to
	 * match in the SAME request — the atomic step the modal's footer notice promises.
	 *
	 * Returns the updated furniture plus the new render `ordering`, exactly like
	 * `updateFurniture`, because a category change re-sorts the furniture's group.
	 *
	 * 400s when the model number has no category at all — surfaced as a normal error
	 * toast, which is why the modal warns with the "No category" tag instead of
	 * disabling the row.
	 */
	async selectArbitraryModelNumber(
		quotationId: number,
		furnitureId: number,
		body: TSelectArbitraryModelNumberRequest,
	) {
		return axiosInstance.patch<TUpdateFurnitureResponse>(
			`quotations/${quotationId}/furniture/${furnitureId}/select-arbitrary-model-number/`,
			body,
		)
	}

	/**
	 * Links (or clears) a custom model number on a furniture (REQ-434..437 / #1346). If the furniture
	 * is not already a custom object, converts it in the SAME request — which is what makes "pick a
	 * custom model number for an existing cabinet" one call rather than a category PATCH followed by
	 * a link.
	 */
	async linkFurnitureCustomModelNumber(
		quotationId: number,
		furnitureId: number,
		body: TLinkFurnitureCustomModelNumberRequest,
	) {
		return axiosInstance.patch<TUpdateFurnitureResponse>(
			`quotations/${quotationId}/furniture/${furnitureId}/custom-model-number/`,
			body,
		)
	}

	async updateFurnitureConfig(quotationId: number, furnitureId: number, body: TUpdateFurnitureConfigRequest) {
		return axiosInstance.patch(`quotations/${quotationId}/furniture/${furnitureId}/configuration/`, body)
	}

	async updateFurnitureComment(quotationId: number, furnitureId: number, body: TUpdateFurnitureCommentRequest) {
		return axiosInstance.put(`quotations/${quotationId}/furniture/${furnitureId}/comment/`, body)
	}

	async deleteFurnitureComment(quotationId: number, furnitureId: number, version: number) {
		return axiosInstance.delete(`quotations/${quotationId}/furniture/${furnitureId}/comment/`, {
			params: { version },
		})
	}

	/**
	 * REQ-413/414. Creates the object's customization when it has none, otherwise overwrites both
	 * of its values — an object can hold at most one, so there is no "add a second" endpoint.
	 * `version` is the furniture's optimistic lock; the response carries the version after the write.
	 */
	async updateFurnitureCustomization(
		quotationId: number,
		furnitureId: number,
		body: TUpdateFurnitureCustomizationRequest,
	) {
		return axiosInstance.put<TUpdateFurnitureCustomizationResponse>(
			`quotations/${quotationId}/furniture/${furnitureId}/customization/`,
			body,
		)
	}

	/** `version` travels as a query param — a DELETE carries no body. 404s when there is none. */
	async deleteFurnitureCustomization(quotationId: number, furnitureId: number, version: number) {
		return axiosInstance.delete<TDeleteFurnitureCustomizationResponse>(
			`quotations/${quotationId}/furniture/${furnitureId}/customization/`,
			{ params: { version } },
		)
	}

	async updateFurnitureStatus(quotationId: number, furnitureId: number, body: TUpdateFurnitureStatusRequest) {
		return axiosInstance.patch(`quotations/${quotationId}/furniture/${furnitureId}/status/`, body)
	}

	async duplicateFurniture(quotationId: number, furnitureId: number, body: TDuplicateFurnitureRequest) {
		return axiosInstance.post<TDuplicateFurnitureResponse>(
			`quotations/${quotationId}/furniture/${furnitureId}/duplicate/`,
			body,
		)
	}

	async batchUpdateFurnitureStatus(quotationId: number, body: TBatchUpdateFurnitureStatusRequest) {
		return axiosInstance.patch<TBatchUpdateFurnitureStatusResponse>(
			`quotations/${quotationId}/furniture/status/`,
			body,
		)
	}

	// ---- Batch furniture edit (REQ-403) -------------------------------------------------------
	// Seven endpoints, all transactional and all-or-nothing (200 or 400), each taking a
	// `furniture: [{ id, version }]` list. A field omitted from the body leaves every item's stored
	// value untouched — that "missing means unchanged" rule is what lets the batch sidebar commit
	// one field at a time over a selection whose other fields differ.

	/** Shared dimensions / model number / name. `anyOf`: exactly one payload field per call. */
	async batchUpdateFurniture(quotationId: number, body: TBatchUpdateFurnitureRequest) {
		return axiosInstance.patch<TBatchUpdateFurnitureResponse>(`quotations/${quotationId}/furniture/`, body)
	}

	/** One category for the whole selection; `related_category_id: null` moves it all to Other. */
	async batchUpdateFurnitureCategory(quotationId: number, body: TBatchUpdateFurnitureCategoryRequest) {
		return axiosInstance.patch<TBatchUpdateFurnitureCategoryResponse>(
			`quotations/${quotationId}/furniture/related-category/`,
			body,
		)
	}

	/**
	 * One model number from ANY category for the whole selection, moving every item's category to
	 * match in the same transaction. The mixed-category path — `batchUpdateFurniture` rejects a
	 * `selected_model_number_id` unless every listed item already shares that category.
	 */
	async batchSelectArbitraryModelNumber(quotationId: number, body: TBatchSelectArbitraryModelNumberRequest) {
		return axiosInstance.patch<TBatchSelectArbitraryModelNumberResponse>(
			`quotations/${quotationId}/furniture/select-arbitrary-model-number/`,
			body,
		)
	}

	/**
	 * One Custom Model Number for the whole selection (#1346), converting every item that is not a
	 * custom object yet — the batch counterpart of `updateFurnitureCustomModelNumber`.
	 */
	async batchUpdateFurnitureCustomModelNumber(
		quotationId: number,
		body: TBatchUpdateFurnitureCustomModelNumberRequest,
	) {
		return axiosInstance.patch<TBatchUpdateFurnitureCustomModelNumberResponse>(
			`quotations/${quotationId}/furniture/custom-model-number/`,
			body,
		)
	}

	/** Attachments config (fillers / finishing ends / locks / shelves). Rejects countertops + Other. */
	async batchUpdateFurnitureConfig(quotationId: number, body: TBatchUpdateFurnitureConfigRequest) {
		return axiosInstance.patch<TBatchUpdateFurnitureConfigResponse>(
			`quotations/${quotationId}/furniture/configuration/`,
			body,
		)
	}

	/**
	 * REQ-449 batch counterpart. Sets Grommets / Wall Cleats for a multi-countertop selection —
	 * transactional, countertop-only, single-page-only.
	 */
	async batchUpdateFurnitureCountertopAttachments(
		quotationId: number,
		body: TBatchUpdateFurnitureCountertopAttachmentsRequest,
	) {
		return axiosInstance.patch<TBatchUpdateFurnitureCountertopAttachmentsResponse>(
			`quotations/${quotationId}/furniture/countertop-attachments/`,
			body,
		)
	}

	/** One fixed price for the whole selection. Other / fixed-price furniture only. */
	async batchUpdateFurnitureFixedPrice(quotationId: number, body: TBatchUpdateFurnitureFixedPriceRequest) {
		return axiosInstance.patch<TBatchUpdateFurnitureFixedPriceResponse>(
			`quotations/${quotationId}/furniture/fixed-price/`,
			body,
		)
	}

	/** One estimation comment for the whole selection. May mix furniture types. */
	async batchUpdateFurnitureComment(quotationId: number, body: TBatchUpdateFurnitureCommentRequest) {
		return axiosInstance.put<TBatchUpdateFurnitureCommentResponse>(
			`quotations/${quotationId}/furniture/comment/`,
			body,
		)
	}

	// Clears the estimation comment of every listed item. A DELETE carries no body, so the batch
	// travels as repeated `items=<furniture_id>:<version>` query params — same serialization trick
	// as `batchDeleteFurniture` below (`indexes: null` → repeated keys, no `[]` brackets).
	async batchDeleteFurnitureComment(quotationId: number, items: string[]) {
		return axiosInstance.delete<TBatchDeleteFurnitureCommentResponse>(
			`quotations/${quotationId}/furniture/comment/`,
			{ params: { items }, paramsSerializer: { indexes: null } },
		)
	}

	/** One customization (REQ-431) for the whole selection, creating or overwriting per item. */
	async batchUpdateFurnitureCustomization(quotationId: number, body: TBatchUpdateFurnitureCustomizationRequest) {
		return axiosInstance.put<TBatchUpdateFurnitureCustomizationResponse>(
			`quotations/${quotationId}/furniture/customization/`,
			body,
		)
	}

	// Clears the customization of every listed item. Same `items=<furniture_id>:<version>` repeated
	// query-param scheme as `batchDeleteFurnitureComment` — a DELETE carries no body.
	async batchDeleteFurnitureCustomization(quotationId: number, items: string[]) {
		return axiosInstance.delete<TBatchDeleteFurnitureCustomizationResponse>(
			`quotations/${quotationId}/furniture/customization/`,
			{ params: { items }, paramsSerializer: { indexes: null } },
		)
	}

	// `items` are `<furniture_id>:<version>` pairs sent as REPEATED query params
	// (`?items=101:3&items=102:0`). `indexes: null` makes axios serialize the array with
	// repeated keys and no `[]` brackets, which is the shape the endpoint expects.
	async batchDeleteFurniture(quotationId: number, items: string[]) {
		return axiosInstance.delete<TBatchDeleteFurnitureResponse>(
			`quotations/${quotationId}/furniture/batch-delete/`,
			{ params: { items }, paramsSerializer: { indexes: null } },
		)
	}

	// On-demand result CSV for an unsubmitted quotation (REQ-420). The response body IS the file
	// (text/csv), so this is the one call in this service that needs `responseType: "blob"` — and its
	// own timeout, because generation is synchronous: a few seconds typically, rarely up to ~20s,
	// which can outrun the shared VITE_AXIOS_INSTANCE_TIMEOUT. Overridden per request so the rest of
	// the app keeps the tighter global value.
	async getResultCsv(quotationId: number) {
		return axiosInstance.get<Blob>(`quotations/${quotationId}/regenerate-result-csv/`, {
			responseType: "blob",
			timeout: RESULT_CSV_TIMEOUT,
		})
	}

	// Starts building the on-demand annotated result PDF (#1401). Runs in the background — the file
	// is not returned here — so the response only carries the `job_id` to poll with
	// `getResultPdfGenerationStatus`. Rejects with 409 once the quotation is submitted, same rule as
	// the result CSV above.
	async startResultPdfGeneration(quotationId: number) {
		return axiosInstance.post<TQuotationResultPdfGenerationResponse>(
			`quotations/${quotationId}/regenerate-result-pdf/`,
			undefined,
			{ timeout: RESULT_PDF_REQUEST_TIMEOUT },
		)
	}

	// Polls one PDF generation job. `download_url` is a temporary presigned link, fresh on every call
	// — only the response that reports `generation_status: "success"` carries a usable one.
	async getResultPdfGenerationStatus(quotationId: number, jobId: number) {
		return axiosInstance.get<TQuotationResultPdfGenerationResponse>(
			`quotations/${quotationId}/regenerate-result-pdf/${jobId}/`,
			{ timeout: RESULT_PDF_REQUEST_TIMEOUT },
		)
	}

	// Starts building the on-demand Quotation Details Word file (#1448). Runs in the background — the
	// file is not returned here — so the response only carries the `job_id` to poll with
	// `getDetailsWordGenerationStatus`. Rejects with 409 once the quotation is submitted, same rule
	// as the result CSV and the annotated result PDF (#1401).
	async startDetailsWordGeneration(quotationId: number) {
		return axiosInstance.post<TQuotationDetailsWordGenerationResponse>(
			`quotations/${quotationId}/generate-details-word/`,
			undefined,
			{ timeout: DETAILS_WORD_REQUEST_TIMEOUT },
		)
	}

	// Polls one Word-file generation job. `download_url` is a temporary presigned link, fresh on
	// every call — only the response that reports `generation_status: "success"` carries a usable one.
	async getDetailsWordGenerationStatus(quotationId: number, jobId: number) {
		return axiosInstance.get<TQuotationDetailsWordGenerationResponse>(
			`quotations/${quotationId}/generate-details-word/${jobId}/`,
			{ timeout: DETAILS_WORD_REQUEST_TIMEOUT },
		)
	}
}

export default new QuotationsService()
